import { useTransformerReportsQuery } from '@/api/queries/transformers/useTransformerReportsQuery'
import { computed, MaybeRefOrGetter, Ref, toValue } from 'vue'
import { AnalysisType, AnalysisTypes } from '@/configuration/analysis/analysis-types'
import { SelectOptions } from '@/components/form-inputs/common/select-option'
import { ListGeneratorAnalysisByTypeResponse, ListGeneratorVisualInspectionsResponse, ListOilAnalysisReportsByTransformerResponse } from '@/api/generated/HCA'
import { useI18n } from 'vue-i18n'
import { useConstantsQuery } from '@/api/queries/general/useConstantsQuery'
import { DATE_FORMAT } from '@/utils/date-utils'
import { format } from 'date-fns'
import { useReportNumbersQuery } from '@/api/queries/reports/useReportNumbersQuery'
import { ComponentType } from '@/configuration/component/component-types'
import { MeasurementType, MeasurementTypes } from '@/configuration/measurements/measurement-types'
import { useGeneratorMeasurementsQuery } from '@/api/queries/generators/useGeneratorMeasurementsQuery'
import { useMeasurementUtils } from '@/composables/useMeasurementUtils'
import { useGeneratorVisualInspectionsQuery } from '@/api/queries/generators/useGeneratorVisualInspectionsQuery'

export const useConditionStatusCommentSelectionHierarchy = (componentId: MaybeRefOrGetter<string>, componentType: MaybeRefOrGetter<ComponentType>, analysisQueriesEnabled: MaybeRefOrGetter<boolean>) => {
    const { t } = useI18n()
    const { getFormattedMeasurement } = useMeasurementUtils()

    const transformerQueriesDisabled = computed(() => !toValue(analysisQueriesEnabled) || toValue(componentType) !== ComponentType.Transformer)
    const generatorQueriesDisabled = computed(() => !toValue(analysisQueriesEnabled) || toValue(componentType) !== ComponentType.Generator)
    const { data: constantsData } = useConstantsQuery()
    const { data: reportNumbersData } = useReportNumbersQuery(componentId, componentType)
    const { data: insulatingOilData } = useTransformerReportsQuery(componentId, AnalysisType.InsulatingOil, transformerQueriesDisabled)
    const { data: furansData } = useTransformerReportsQuery(componentId, AnalysisType.Furans, transformerQueriesDisabled)
    const { data: gasInOilData } = useTransformerReportsQuery(componentId, AnalysisType.GasInOil, transformerQueriesDisabled)
    const { data: visualInspectionData } = useGeneratorVisualInspectionsQuery(componentId)
    const { data: windingResistanceData } = useGeneratorMeasurementsQuery(componentId, MeasurementType.WindingResistance, generatorQueriesDisabled)
    const { data: poleImpedanceData } = useGeneratorMeasurementsQuery(componentId, MeasurementType.PoleImpedance, generatorQueriesDisabled)
    const { data: isolationImpedanceStatorData } = useGeneratorMeasurementsQuery(componentId, MeasurementType.IsolationImpedanceStator, generatorQueriesDisabled)
    const { data: isolationImpedanceRotorData } = useGeneratorMeasurementsQuery(componentId, MeasurementType.IsolationImpedanceRotor, generatorQueriesDisabled)
    const { data: lossFactorData } = useGeneratorMeasurementsQuery(componentId, MeasurementType.LossFactor, generatorQueriesDisabled)
    const { data: partialDischargeData } = useGeneratorMeasurementsQuery(componentId, MeasurementType.PartialDischarge, generatorQueriesDisabled)
    const { data: voltageTestingData } = useGeneratorMeasurementsQuery(componentId, MeasurementType.VoltageTesting, generatorQueriesDisabled)
    const { data: inductionTestingData } = useGeneratorMeasurementsQuery(componentId, MeasurementType.InductionTesting, generatorQueriesDisabled)
    const { data: heatingTestData } = useGeneratorMeasurementsQuery(componentId, MeasurementType.HeatingTest, generatorQueriesDisabled)
    const { data: vibrationsData } = useGeneratorMeasurementsQuery(componentId, MeasurementType.Vibrations, generatorQueriesDisabled)

    const extractMeasurementsFromReportData = (
        reportNumber: string,
        analysisType: AnalysisType | MeasurementType,
        data?: ListOilAnalysisReportsByTransformerResponse | ListGeneratorAnalysisByTypeResponse,
    ): SelectOptions<number> => {
        const parameters = data?.reports?.find((e) => e.analyticalNumber === reportNumber)?.parameters
        const constantsParameters = constantsData.value?.parameters

        if (!parameters || !constantsParameters) {
            return []
        }

        const parameterIds = parameters.map((e) => ({
            parameterId: e.parameterId,
            unit: e.unit,
            tableNumber: e.tableNumber,
        })).filter((e) => !!e)

        return parameterIds
            .map((parameter) => {
                const constantParameter = constantsParameters.find((e) => e.id === parameter.parameterId)

                if (!constantParameter) {
                    return undefined
                }

                return getFormattedMeasurement(constantParameter, analysisType, parameter.tableNumber, parameter.unit)
            })
            .filter((e) => !!e) as SelectOptions<number>
    }

    const extractVisualInspectionMeasurementsFromReportData = (
        reportNumber: string,
        data?: ListGeneratorVisualInspectionsResponse,
    ): SelectOptions<number> => {
        const parameters = data?.items?.find((e) => e.analyticalNumber === reportNumber)?.inspectionParameterValues
        const constantsParameters = constantsData.value?.visualInspectionParameters

        if (!parameters || !constantsParameters) {
            return []
        }

        const parameterIds = parameters.map((e) => ({
            parameterId: e.parameterId,
        })).filter((e) => !!e)

        return parameterIds
            .map((parameter) => {
                const constantParameter = constantsParameters.find((e) => e.id === parameter.parameterId)

                if (!constantParameter) {
                    return undefined
                }

                return {
                    label: t(`transformers.conditionStatus.content.visualInspections.table.measurementPoints.${constantParameter.name}`),
                    value: constantParameter.id,
                }
            })
            .filter((e) => !!e) as SelectOptions<number>
    }

    const isReportNumberInData = (
        reportNumber: string,
        data?: ListOilAnalysisReportsByTransformerResponse,
    ): boolean => {
        return !!data?.reports?.some((e) => e.analyticalNumber === reportNumber)
    }

    const reportNumberOptions = computed<SelectOptions>(
        () =>
            reportNumbersData.value?.reportNumbers?.map((e) => {
                const id = e.id ?? '-'

                return {
                    label: e.date ? `${id} (${format(e.date, DATE_FORMAT)})` : id,
                    value: id,
                }
            }) ?? [],
    )

    const isReportNumberInVisualInspectionData = (
        reportNumber: string,
        data?: ListGeneratorVisualInspectionsResponse,
    ): boolean => {
        return !!data?.items?.some((e) => e.analyticalNumber === reportNumber)
    }

    const createComputedAnalysisTypeOptions = (reportNumber: Ref<string>) => {
        //TODO: extend when new analysis/measurement types are added
        return computed<SelectOptions<number>>(() => {
            const options: SelectOptions<number> = []

            if (isReportNumberInData(reportNumber.value, insulatingOilData.value)) {
                options.push({
                    label: t(AnalysisTypes.find((e) => e.value === AnalysisType.InsulatingOil)?.labelKey ?? ''),
                    value: AnalysisType.InsulatingOil,
                })
            }

            if (isReportNumberInData(reportNumber.value, furansData.value)) {
                options.push({
                    label: t(AnalysisTypes.find((e) => e.value === AnalysisType.Furans)?.labelKey ?? ''),
                    value: AnalysisType.Furans,
                })
            }

            if (isReportNumberInVisualInspectionData(reportNumber.value, visualInspectionData.value)) {
                options.push({
                    label: t(AnalysisTypes.find((e) => e.value === AnalysisType.VisualInspection)?.labelKey ?? ''),
                    value: AnalysisType.VisualInspection,
                })
            }

            if (isReportNumberInData(reportNumber.value, gasInOilData.value)) {
                options.push({
                    label: t(AnalysisTypes.find((e) => e.value === AnalysisType.GasInOil)?.labelKey ?? ''),
                    value: AnalysisType.GasInOil,
                })
            }

            if (isReportNumberInData(reportNumber.value, windingResistanceData.value)) {
                options.push({
                    label: t(MeasurementTypes.find((e) => e.value === MeasurementType.WindingResistance)?.labelKey ?? ''),
                    value: MeasurementType.WindingResistance,
                })
            }

            if (isReportNumberInData(reportNumber.value, poleImpedanceData.value)) {
                options.push({
                    label: t(MeasurementTypes.find((e) => e.value === MeasurementType.PoleImpedance)?.labelKey ?? ''),
                    value: MeasurementType.PoleImpedance,
                })
            }

            if (isReportNumberInData(reportNumber.value, isolationImpedanceStatorData.value)) {
                options.push({
                    label: t(MeasurementTypes.find((e) => e.value === MeasurementType.IsolationImpedanceStator)?.labelKey ?? ''),
                    value: MeasurementType.IsolationImpedanceStator,
                })
            }

            if (isReportNumberInData(reportNumber.value, isolationImpedanceRotorData.value)) {
                options.push({
                    label: t(MeasurementTypes.find((e) => e.value === MeasurementType.IsolationImpedanceRotor)?.labelKey ?? ''),
                    value: MeasurementType.IsolationImpedanceRotor,
                })
            }

            if (isReportNumberInData(reportNumber.value, lossFactorData.value)) {
                options.push({
                    label: t(MeasurementTypes.find((e) => e.value === MeasurementType.LossFactor)?.labelKey ?? ''),
                    value: MeasurementType.LossFactor,
                })
            }

            if (isReportNumberInData(reportNumber.value, partialDischargeData.value)) {
                options.push({
                    label: t(MeasurementTypes.find((e) => e.value === MeasurementType.PartialDischarge)?.labelKey ?? ''),
                    value: MeasurementType.PartialDischarge,
                })
            }

            if (isReportNumberInData(reportNumber.value, voltageTestingData.value)) {
                options.push({
                    label: t(MeasurementTypes.find((e) => e.value === MeasurementType.VoltageTesting)?.labelKey ?? ''),
                    value: MeasurementType.VoltageTesting,
                })
            }

            if (isReportNumberInData(reportNumber.value, inductionTestingData.value)) {
                options.push({
                    label: t(MeasurementTypes.find((e) => e.value === MeasurementType.InductionTesting)?.labelKey ?? ''),
                    value: MeasurementType.InductionTesting,
                })
            }

            if (isReportNumberInData(reportNumber.value, heatingTestData.value)) {
                options.push({
                    label: t(MeasurementTypes.find((e) => e.value === MeasurementType.HeatingTest)?.labelKey ?? ''),
                    value: MeasurementType.HeatingTest,
                })
            }

            if (isReportNumberInData(reportNumber.value, vibrationsData.value)) {
                options.push({
                    label: t(MeasurementTypes.find((e) => e.value === MeasurementType.Vibrations)?.labelKey ?? ''),
                    value: MeasurementType.Vibrations,
                })
            }

            return options
        })
    }

    const createComputedMeasurementOptions = (reportNumber: Ref<string>, analysisType: Ref<number | undefined>) => {
        //TODO: extend when new analysis/measurement types are added
        return computed(() => {
            switch (analysisType.value) {
                case AnalysisType.GasInOil:
                    return extractMeasurementsFromReportData(reportNumber.value, analysisType.value, gasInOilData.value)
                case AnalysisType.Furans:
                    return extractMeasurementsFromReportData(reportNumber.value, analysisType.value, furansData.value)
                case AnalysisType.InsulatingOil:
                    return extractMeasurementsFromReportData(reportNumber.value, analysisType.value, insulatingOilData.value)
                case AnalysisType.VisualInspection:
                    return extractVisualInspectionMeasurementsFromReportData(reportNumber.value, visualInspectionData.value)
                case MeasurementType.WindingResistance:
                    return extractMeasurementsFromReportData(reportNumber.value, analysisType.value, windingResistanceData.value)
                case MeasurementType.PoleImpedance:
                    return extractMeasurementsFromReportData(reportNumber.value, analysisType.value, poleImpedanceData.value)
                case MeasurementType.IsolationImpedanceStator:
                    return extractMeasurementsFromReportData(reportNumber.value, analysisType.value, isolationImpedanceStatorData.value)
                case MeasurementType.IsolationImpedanceRotor:
                    return extractMeasurementsFromReportData(reportNumber.value, analysisType.value, isolationImpedanceRotorData.value)
                case MeasurementType.LossFactor:
                    return extractMeasurementsFromReportData(reportNumber.value, analysisType.value, lossFactorData.value)
                case MeasurementType.PartialDischarge:
                    return extractMeasurementsFromReportData(reportNumber.value, analysisType.value, partialDischargeData.value)
                case MeasurementType.VoltageTesting:
                    return extractMeasurementsFromReportData(reportNumber.value, analysisType.value, voltageTestingData.value)
                case MeasurementType.InductionTesting:
                    return extractMeasurementsFromReportData(reportNumber.value, analysisType.value, inductionTestingData.value)
                case MeasurementType.HeatingTest:
                    return extractMeasurementsFromReportData(reportNumber.value, analysisType.value, heatingTestData.value)
                case MeasurementType.Vibrations:
                    return extractMeasurementsFromReportData(reportNumber.value, analysisType.value, vibrationsData.value)
                default:
                    return []
            }
        })
    }

    return {
        reportNumberOptions,
        createComputedAnalysisTypeOptions,
        createComputedMeasurementOptions,
    }
}
